// Find-references results workspace tab — Phase 3 Slice 24.
//
// Renders `FindReferencesResultGroup[]` with one collapsible section per
// referenced object. Each row shows a code-snippet preview pulled lazily from
// `fetchObjectBody`. Clicking a row opens the host object's CREATE-text in a
// query tab, scrolled to the reference range.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Code, FileText, Search } from 'lucide-react';

import {
  isReferencesTab,
  type WorkspaceTabComponentProps,
} from '../../workspace/core/types/workspaceTypes';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import {
  fetchObjectBody,
  isParseableObjectKind,
} from '../index/objectBodyFetcher';
import type { ObjectKind, QualifiedName, SqlRange } from '../types';

import { bodyKey, type ReferencesTabContent } from './referencesTabTypes';
import { openReferenceLocation } from './openReferenceLocation';
import {
  ReferencesEmptyState,
  ReferencesErrorState,
  ReferencesSearchingState,
} from './ReferencesEmptyState';

/** No results yet AND the opener is still waiting for the database's index —
 *  distinct from a `loading` status with groups present, which is the
 *  per-group body fetch. */
function isSearching(content: ReferencesTabContent): boolean {
  return content.status === 'loading' && content.groups.length === 0;
}

export function ReferencesTab({ tabId, className }: WorkspaceTabComponentProps): React.ReactElement {
  const tab = useWorkspaceStore((state) => state.tabs.find((t) => t.id === tabId));
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  const content: ReferencesTabContent | null =
    tab && isReferencesTab(tab) ? tab.content : null;

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Refs let us read the latest content snapshot without forcing the fetch
  // effect to re-run when `content` changes — the effect would otherwise
  // cancel its own in-flight fetch every time it writes back to the tab.
  const contentRef = useRef(content);
  contentRef.current = content;
  const groupsKey = content
    ? content.groups.map((g) => bodyKey(g.objectKind, g.objectName)).join('|')
    : '';

  // Fetch object bodies once per group set — Slice 24 only renders snippets
  // for objects whose body the introspection feed knows how to pull (views /
  // inline TVFs / scalar funcs / default + check constraints). Procedural
  // bodies (procs / triggers) render with no snippet and only show the
  // range.
  //
  // Each run owns its own `cancelled` flag and nothing else gates a new run:
  // when the group set changes mid-fetch (the opener refills a reused tab —
  // searching state, then groups again — while the previous fetch is still
  // waiting on IPC) the stale run is cancelled and discards its result, and
  // the new run fetches for the new groups. A shared "in flight" guard used to
  // make the new run bail out and leave the refilled groups without snippets.
  useEffect(() => {
    if (!content) return;
    const missing = content.groups.filter((g) =>
      isParseableObjectKind(g.objectKind) && !content.bodies[bodyKey(g.objectKind, g.objectName)],
    );
    if (missing.length === 0) return;

    let cancelled = false;
    void (async () => {
      updateTabContent<'references-tab'>(tabId, { status: 'loading' });
      const next: Record<string, string> = { ...content.bodies };
      for (const group of missing) {
        try {
          const body = await fetchObjectBody({
            connectionId: content.connectionId,
            database: content.database,
            engineId: content.engineId,
            objectKind: group.objectKind,
            objectName: group.objectName,
          });
          if (cancelled) return;
          if (body !== null) {
            next[bodyKey(group.objectKind, group.objectName)] = body;
          }
        } catch {
          // Best-effort — missing body means the row renders without a
          // snippet.
        }
      }
      if (cancelled) return;
      updateTabContent<'references-tab'>(tabId, {
        bodies: next,
        status: 'ready',
      });
    })();

    return () => {
      cancelled = true;
    };
    // We re-fetch only when the *set of group keys* changes — avoids
    // self-cancellation when our own writes update content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey, tabId, updateTabContent]);

  if (!content) {
    return (
      <div className={className} data-testid="references-tab-missing">
        <div className="p-4 text-sm text-muted-foreground">References tab content unavailable.</div>
      </div>
    );
  }

  const totalCount = content.groups.reduce((acc, g) => acc + g.references.length, 0);

  return (
    <div
      className={className ?? ''}
      data-testid="references-tab"
      data-references-count={totalCount}
    >
      <ReferencesHeader content={content} totalCount={totalCount} />
      <div className="p-3 space-y-2">
        {isSearching(content) ? (
          <ReferencesSearchingState
            identifier={content.identifier}
            engineId={content.engineId}
            database={content.database}
          />
        ) : content.status === 'error' ? (
          <ReferencesErrorState message={content.error} />
        ) : content.groups.length === 0 ? (
          <ReferencesEmptyState
            identifier={content.identifier}
            identifierKind={content.identifierKind}
            engineId={content.engineId}
            database={content.database}
          />
        ) : (
          content.groups.map((group) => {
            const key = bodyKey(group.objectKind, group.objectName);
            return (
              <ReferenceGroupView
                key={key}
                groupKey={key}
                content={content}
                objectKind={group.objectKind}
                objectName={group.objectName}
                references={group.references}
                isCollapsed={collapsed[key] === true}
                onToggle={() =>
                  setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
                }
              />
            );
          })
        )}
      </div>
    </div>
  );
}

type ReferencesHeaderProps = {
  readonly content: ReferencesTabContent;
  readonly totalCount: number;
};

function ReferencesHeader({ content, totalCount }: ReferencesHeaderProps): React.ReactElement {
  return (
    <div className="border-b border-border px-3 py-2 flex items-center gap-2">
      <Search className="h-4 w-4 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate" data-testid="references-target-label">
          References to {content.identifierKind === 'column' ? 'column' : content.identifierKind}{' '}
          <code className="px-1 rounded bg-muted text-foreground">
            {formatIdentifier(content.identifier)}
          </code>
          {content.relation ? (
            <>
              {' '}on{' '}
              <code className="px-1 rounded bg-muted text-foreground">
                {formatIdentifier(content.relation)}
              </code>
            </>
          ) : null}
        </div>
      </div>
      <div
        className="text-xs text-muted-foreground"
        data-testid="references-summary"
      >
        {isSearching(content) ? (
          'Searching…'
        ) : content.status === 'error' ? (
          // "0 references" would read as an answer; nothing was searched.
          'Index unavailable'
        ) : (
          <>
            {totalCount} reference{totalCount === 1 ? '' : 's'} in {content.groups.length} object{content.groups.length === 1 ? '' : 's'}
          </>
        )}
        {content.partial ? (
          // The identifier index stopped short of the whole catalog (body-byte
          // budget / row ceiling — see index/navIndexBudget.ts), so this list
          // can only under-report.
          <span data-testid="references-partial-note"> · results may be incomplete</span>
        ) : null}
      </div>
    </div>
  );
}

type ReferenceGroupViewProps = {
  readonly groupKey: string;
  readonly content: ReferencesTabContent;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
  readonly references: readonly { readonly range: SqlRange; readonly referenceKind: string; readonly parseStatus: string }[];
  readonly isCollapsed: boolean;
  readonly onToggle: () => void;
};

function ReferenceGroupView({
  groupKey,
  content,
  objectKind,
  objectName,
  references,
  isCollapsed,
  onToggle,
}: ReferenceGroupViewProps): React.ReactElement {
  const body = content.bodies[groupKey] ?? '';
  const handleClick = useCallback(
    (range: SqlRange) => {
      void openReferenceLocation({
        connectionId: content.connectionId,
        database: content.database,
        engineId: content.engineId,
        objectKind,
        objectName,
        range,
        bodyText: body,
      });
    },
    [body, content.connectionId, content.database, content.engineId, objectKind, objectName],
  );
  return (
    <div
      className="border border-border rounded"
      data-testid="references-group"
      data-object-kind={objectKind}
      data-object-name={objectName.name}
    >
      <button
        type="button"
        className="w-full px-2 py-1.5 flex items-center gap-2 hover:bg-muted text-left"
        onClick={onToggle}
        data-testid="references-group-toggle"
      >
        {isCollapsed ? (
          <ChevronRight className="h-3.5 w-3.5 flex-none" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 flex-none" />
        )}
        {iconForObjectKind(objectKind)}
        <span className="text-sm font-medium truncate">
          {formatObjectKindLabel(objectKind)} ·{' '}
          <code>{formatIdentifier(objectName)}</code>
        </span>
        <span
          className="text-xs text-muted-foreground"
          data-testid="references-group-count"
        >
          {references.length}
        </span>
      </button>
      {isCollapsed ? null : (
        <ul className="divide-y divide-border">
          {references.map((ref, idx) => (
            <li key={`${idx}-${ref.range.startLine}-${ref.range.startColumn}`}>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 hover:bg-muted flex items-baseline gap-3"
                onClick={() => handleClick(ref.range)}
                data-testid="references-row"
                data-line={ref.range.startLine + 1}
                data-column={ref.range.startColumn + 1}
              >
                <span className="font-mono text-xs text-muted-foreground tabular-nums w-12 flex-none">
                  {ref.range.startLine + 1}:{ref.range.startColumn + 1}
                </span>
                <code className="text-xs flex-1 truncate" data-testid="references-snippet">
                  {renderSnippet(body, ref.range)}
                </code>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function iconForObjectKind(kind: ObjectKind): React.ReactElement {
  if (kind === 'view' || kind === 'materialized-view') return <Code className="h-3.5 w-3.5 text-green-500" />;
  if (kind === 'inline-tvf' || kind === 'scalar-function') return <Code className="h-3.5 w-3.5 text-purple-500" />;
  if (kind === 'procedure') return <Code className="h-3.5 w-3.5 text-orange-500" />;
  if (kind === 'trigger') return <Code className="h-3.5 w-3.5 text-yellow-500" />;
  return <FileText className="h-3.5 w-3.5 text-muted-foreground" />;
}

function formatObjectKindLabel(kind: ObjectKind): string {
  switch (kind) {
    case 'view':
      return 'View';
    case 'materialized-view':
      return 'Materialized View';
    case 'inline-tvf':
      return 'Inline TVF';
    case 'scalar-function':
      return 'Scalar Function';
    case 'procedure':
      return 'Procedure';
    case 'trigger':
      return 'Trigger';
    case 'default-constraint':
      return 'Default Constraint';
    case 'check-constraint':
      return 'Check Constraint';
  }
}

function formatIdentifier(name: QualifiedName): string {
  if (name.schema && name.name) return `${name.schema}.${name.name}`;
  return name.name;
}

function renderSnippet(body: string, range: SqlRange): string {
  if (!body) return '(no preview available)';
  const lines = body.split('\n');
  const line = lines[range.startLine] ?? '';
  if (line.length <= 120) return line;
  // Center the snippet around the start of the range so the reference is
  // visible even on very long lines.
  const start = Math.max(0, range.startColumn - 40);
  const end = Math.min(line.length, range.startColumn + 80);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < line.length ? '…' : '';
  return `${prefix}${line.slice(start, end)}${suffix}`;
}

